import { useState, useEffect } from "react";
import { GlossaryBlueprint } from "@/types-games";
import { sectionDataObject } from "@/lib/stores/objectsStore";
import {
  detectBlueprintChanges,
  BlueprintVersionChanges,
} from "@/lib/utils/blueprintVersionUtils";
import {
  extractBlueprintVersion,
  versionsMatch,
} from "@/lib/utils/versionUtils";
import { WizardMode } from "../types";

interface UseBlueprintVersionManagementProps {
  initialData: any;
  selectedBlueprint: GlossaryBlueprint | null;
  parsedSections: sectionDataObject[];
  wizardMode: WizardMode;
}

/**
 * Hook to manage blueprint version detection and mismatch handling
 * Detects when an object's blueprint version differs from the current blueprint
 */
export function useBlueprintVersionManagement({
  initialData,
  selectedBlueprint,
  parsedSections,
  wizardMode,
}: UseBlueprintVersionManagementProps) {
  // State for blueprint version mismatch detection
  const [blueprintChanges, setBlueprintChanges] =
    useState<BlueprintVersionChanges | null>(null);

  // Track the current blueprint version to detect when it's been updated
  // Convert to comparable number format for consistency
  const [currentItemBlueprintVersion, setCurrentItemBlueprintVersion] =
    useState<
      number | { major: number; minor: number; patch: number } | undefined
    >(initialData?.blueprint_version);

  // Detect blueprint version mismatches for display warnings
  useEffect(() => {
    if (
      initialData &&
      selectedBlueprint &&
      parsedSections &&
      currentItemBlueprintVersion !== undefined
    ) {
      // Extract comparable version numbers
      const currentBlueprintVersionNumber = extractBlueprintVersion(
        selectedBlueprint
      );

      // Convert item's blueprint version to comparable number
      let itemVersionNumber: number | undefined;
      if (typeof currentItemBlueprintVersion === "number") {
        itemVersionNumber = currentItemBlueprintVersion;
      } else if (
        currentItemBlueprintVersion &&
        typeof currentItemBlueprintVersion === "object" &&
        "major" in currentItemBlueprintVersion
      ) {
        itemVersionNumber =
          currentItemBlueprintVersion.major * 1000000 +
          currentItemBlueprintVersion.minor * 1000 +
          currentItemBlueprintVersion.patch;
      }

      // Only detect changes if versions differ
      if (!versionsMatch(currentBlueprintVersionNumber, itemVersionNumber)) {
        const changes = detectBlueprintChanges(
          selectedBlueprint,
          currentItemBlueprintVersion,
          parsedSections
        );
        setBlueprintChanges(changes);
      } else {
        setBlueprintChanges(null);
      }
    } else {
      setBlueprintChanges(null);
    }
  }, [
    initialData,
    selectedBlueprint,
    parsedSections,
    wizardMode,
    currentItemBlueprintVersion,
  ]);

  return {
    blueprintChanges,
    currentItemBlueprintVersion,
    setCurrentItemBlueprintVersion,
    setBlueprintChanges,
  };
}

